import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { getWindDirectionIcon, isInViewport } from "Utils/utils";
import InfoDialog from "Molecules/InfoDialog";
import "Assets/styles/dmi-style/directly-included/weather-by-hours.css";
import SunriseIcon from "Assets/img/sunrise.svg";
import SunsetIcon from "Assets/img/sunset.svg";
import ExpandIconUp from "Assets/img/expand-icon-up.svg";
import ExpandIconRight from "Assets/img/expand-icon-right.svg";
import { mainHeadings, hourHeadings } from "Utils/weatherByHoursHeadings";

const WeatherByHoursAccordion = ({ weatherData }) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);

    // Due to material ui bug in v4 element scrolls out of view when expanded.
    // A little hack to make sure to await until animation is finished and then check whether element is visible or not.
    // If not visible, it will scroll to align the element to top of viewport.
    // Remove the  following code on upgrade to mui 5  (after testing that it is fixed off course).
    setTimeout(() => {
      if (isExpanded) {
        const topElement = document.getElementById(
          `${panel}-accordionitem-day`
        );
        const expandedPanel = document.getElementById(`${panel}-hourheadings`);
        const expandedPanelIsVisible = isInViewport(expandedPanel);
        const topElementIsVisible = isInViewport(topElement);
        if (!expandedPanelIsVisible || !topElementIsVisible) {
          topElement.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 350);
  };

  return (
    <>
      {!!weatherData.length && (
        <div className="root">
          {/* Begin: Main headings */}
          <div className="main-headings-row">
            {mainHeadings.map((heading, index) => (
              <p
                key={`${index}-mainheading`}
                className={heading.class.join(" ")}
              >
                {heading.heading}
                {heading.info && (
                  <InfoDialog
                    title="UV indekset og UV maks"
                    text="UV-indekset er et udtryk for intensiteten af solens ultraviolette stråling og
                    dermed, hvornår du skal passe på i solen. UV MAKS dækker over det højest
                    mulige UV-indeks under skyfri betingelser."
                    link="https://www.dmi.dk/vejr-og-atmosfare/temaforside-ozonlaget-og-uv-straaling/"
                  />
                )}
              </p>
            ))}
          </div>
          {/* End: Main headings */}
          {weatherData.map(
            (
              {
                warning,
                longFullDate,
                hours,
                maxTemp,
                minTemp,
                windSpeed,
                iconWeather,
                precip,
                humidity,
                symbol,
                uv,
                daylightChangeString,
                sunUpTime,
                sunDownTime,
                timeSpanText,
                shortDay,
                shortDateMonth,
              },
              index
            ) => (
              <Accordion
                key={`${index}-accordionitem-day`}
                expanded={expanded === index}
                onChange={handleChange(index)}
                slotProps={{ transition: { unmountOnExit: true } }}
              >
                <AccordionSummary
                  aria-controls={`${index}-content`}
                  id={`${index}-accordionitem-day`}
                >
                  {/* Begin: day overview/summary */}
                  {expanded !== index && (
                    <>
                      <p
                        className={`bold-font xl-column column-base-style wrap`}
                        style={{ paddingRight: "5px" }}
                      >
                        <span className="hide-on-larger-than-6">
                          {shortDay}
                          <br />
                          {shortDateMonth}
                        </span>
                        <span className="hide-on-smaller-than-6">
                          {longFullDate}
                        </span>
                        {!warning && timeSpanText && (
                          <span className="secondary-data">{timeSpanText}</span>
                        )}
                        {warning && (
                          <>
                            {" "}
                            {timeSpanText && (
                              <span className="hide-on-smaller-than-3 secondary-data">
                                {timeSpanText}
                              </span>
                            )}
                            <span className="hide-on-larger-than-3">
                              <>
                                {" "}
                                <img
                                  className="varsels-ikon"
                                  alt={`Varselsikon ${warning}`}
                                  style={{ marginRight: "10px" }}
                                  src={`assets/img/Varselsikoner/${warning}`}
                                  width="25"
                                  height="25"
                                />
                              </>
                            </span>
                          </>
                        )}
                      </p>
                      <p className="x-small-column column-base-style hide-on-smaller-than-3">
                        {warning && (
                          <>
                            {" "}
                            <img
                              className="varsels-ikon"
                              style={{ marginLeft: "5px" }}
                              alt={`Varselsikon ${warning}`}
                              src={`assets/img/Varselsikoner/${warning}`}
                              width="25"
                              height="25"
                            />
                          </>
                        )}
                      </p>
                      <p className="large-column column-base-style">
                        {symbol && (
                          <img
                            alt={`${iconWeather} ikon`}
                            src={`assets/img/${symbol}.svg`}
                            className="weather-icon"
                          />
                        )}
                        <span className={"large-data"}>
                          {maxTemp}&deg;/{minTemp}&deg;
                        </span>
                      </p>
                      <p className="small-column column-base-style">{precip}</p>
                      <p className="small-column column-base-style">
                        {windSpeed !== null && <>{windSpeed} m/s</>}
                      </p>
                      <p className="small-column column-base-style hide-on-smaller-than-4">
                        <span
                          className={`uv bold-font`}
                          style={{
                            border: `2px solid ${uv.color}`,
                          }}
                        >
                          {uv.value}
                        </span>
                      </p>
                      <p className="small-column column-base-style hide-on-smaller-than-4">
                        {humidity && <>{humidity}%</>}
                      </p>

                      <p className="large-column column-base-style open-text">
                        <span className="hide-on-smaller-than-6">
                          Time for time{" "}
                        </span>
                        <img
                          className="arrow-icon"
                          alt="åbn-timevisning-ikon"
                          src={ExpandIconRight}
                        />
                      </p>
                    </>
                  )}
                  {/* End: day overview/summary */}
                  {/* Begin: Expanded day overview/summary */}
                  {expanded === index && (
                    <>
                      <p
                        className={`column-base-style xl-column expanded-side-cols bold-font`}
                      >
                        <span className="hide-on-larger-than-4">
                          {" "}
                          {shortDay}
                          <br />
                          {shortDateMonth}
                        </span>
                        <span className="hide-on-smaller-than-4">
                          {longFullDate}
                        </span>
                      </p>
                      {/* Begin: Sun up and down and UV for large screens - expanded */}
                      <span className="hide-on-smaller-than-6">
                        <p className="centered-col">
                          {warning && (
                            <>
                              {" "}
                              <img
                                className="varsels-ikon"
                                style={{ marginRight: "10px" }}
                                alt={`Varselsikon ${warning}`}
                                src={`assets/img/Varselsikoner/${warning}`}
                                width="25"
                                height="25"
                              />
                            </>
                          )}
                          {sunUpTime && (
                            <span className="center-col-item">
                              <img
                                className="sun-icon-large-screen"
                                alt="solopgangsikon"
                                src={SunriseIcon}
                                width="20px"
                                height="20px"
                              />
                              <span className="bold-font">{sunUpTime}</span>
                            </span>
                          )}
                          {sunDownTime && (
                            <span className="center-col-item">
                              <img
                                className="sun-icon-large-screen"
                                alt="solnedgangsikon"
                                src={SunsetIcon}
                                width="20px"
                                height="20px"
                              />
                              <span className="bold-font">{sunDownTime}</span>
                            </span>
                          )}
                          {daylightChangeString && (
                            <span className="center-col-item">
                              {daylightChangeString}
                            </span>
                          )}
                          <span className="center-col-item">
                            UV maks
                            <span
                              className={`uv bold-font`}
                              style={{
                                border: `2px solid ${uv.color}`,
                                marginLeft: `5px`,
                              }}
                            >
                              {uv.value}
                            </span>
                          </span>
                        </p>
                      </span>
                      {/* End: Sun up and down and UV for large screens - expanded*/}
                      <p
                        className={`column-base-style xl-column expanded-side-cols`}
                      >
                        <span className="hide-on-smaller-than-4">Luk</span>
                        <img
                          className="close-icon"
                          alt="luk-timevisning-ikon"
                          src={ExpandIconUp}
                        />
                      </p>
                    </>
                  )}
                  {/* End: Expanded day overview/summary */}
                </AccordionSummary>
                {/* Begin: Sun up and down and UV for small screens */}
                <span className="hide-on-larger-than-6">
                  <AccordionDetails>
                    <p className="flex-col">
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        {sunUpTime && (
                          <span className="center-col-item">
                            <img
                              className="sun-icon-small-screen"
                              alt="solopgangsikon"
                              src={SunriseIcon}
                              width="20px"
                              height="20px"
                            />
                            <span className="bold-font">{sunUpTime}</span>
                          </span>
                        )}
                        {sunDownTime && (
                          <span className="center-col-item">
                            <img
                              className="sun-icon-small-screen"
                              alt="solnedgangsikon"
                              src={SunsetIcon}
                              width="20px"
                              height="20px"
                            />
                            <span className="bold-font">{sunDownTime}</span>
                          </span>
                        )}
                      </span>
                      {daylightChangeString && (
                        <span className="center-col-item">
                          {daylightChangeString}
                        </span>
                      )}
                      <span className="center-col-item">
                        UV maks
                        <span
                          className={`uv bold-font`}
                          style={{
                            border: `2px solid ${uv.color}`,
                            marginLeft: `2px`,
                          }}
                        >
                          {uv.value}
                        </span>
                        {humidity && (
                          <span
                            style={{
                              marginLeft: `15px`,
                            }}
                          >
                            Luftfugtighed {humidity}%
                          </span>
                        )}
                      </span>
                    </p>
                  </AccordionDetails>
                </span>
                {/* End: Sun up and down and UV for small screens */}
                {/* Begin: Hour headings */}
                <AccordionDetails
                  id={`${index}-hourheadings`}
                  key={`${index}-hourheadings`}
                  className="hour-row"
                >
                  {hourHeadings.map((heading, index) => (
                    <p
                      key={`${index}-hourheading`}
                      className={heading.class.join(" ")}
                    >
                      {heading.heading}
                      {heading.headingSub && (
                        <>
                          <br />
                          {heading.headingSub}
                        </>
                      )}
                    </p>
                  ))}
                </AccordionDetails>
                {/* End: Hour headings */}
                {/* Begin: Hour details in expanded panel */}
                {hours &&
                  hours.length &&
                  hours.map(
                    (
                      {
                        sunUpTime,
                        sunDownTime,
                        hour,
                        warnings,
                        iconWeather,
                        symbol,
                        temperature,
                        feelsLike,
                        precip,
                        windSpeed,
                        windGust,
                        windDegree,
                        humidity,
                      },
                      index
                    ) => (
                      <AccordionDetails
                        key={`${index}-hourdetails`}
                        className="hour-row"
                      >
                        <p
                          className={`small-column hide-on-smaller-than-5 sun-up-down-col`}
                        >
                          {sunDownTime && (
                            <>
                              <span className="secondary-data bold-font">
                                {sunDownTime}
                              </span>
                              <img
                                className="sun-icon-expanded"
                                alt="solnedgangsikon"
                                src={SunsetIcon}
                                width="20px"
                                height="20px"
                              />
                            </>
                          )}
                          {sunUpTime && (
                            <>
                              <span className="secondary-data bold-font">
                                {sunUpTime}
                              </span>
                              <img
                                className="sun-icon-expanded"
                                alt="solopgangsikon"
                                src={SunriseIcon}
                                width="20px"
                                height="20px"
                              />
                            </>
                          )}
                        </p>
                        <p className="small-column column-base-style hide-on-smaller-than-4">
                          {hour}:00
                        </p>
                        <p className="x-small-column column-base-style hide-on-larger-than-4">
                          {hour}:00
                        </p>
                        <p className="x-small-column column-base-style ">
                          {warnings &&
                            warnings.icon &&
                            warnings.warningList && (
                              <img
                                className="varsels-ikon"
                                style={{ marginLeft: "5px" }}
                                src={`assets/img/Varselsikoner/${warnings.icon}`}
                                alt={warnings.warningList[0].title}
                                title={warnings.warningList[0].title}
                                width="25"
                                height="25"
                              />
                            )}
                        </p>
                        <p className="large-column column-base-style ">
                          {symbol && (
                            <img
                              alt={`${iconWeather} ikon`}
                              src={`assets/img/${symbol}.svg`}
                              className="weather-icon"
                            />
                          )}
                          {temperature}&deg;{" "}
                          <span className="secondary-data">
                            &nbsp;({feelsLike}&deg;)
                          </span>
                        </p>
                        <p className="small-column column-base-style ">
                          {precip}
                        </p>
                        {/* Begin: Wind */}
                        <p className="wind-col">
                          {/* Begin: Wind on small screens */}
                          <span className="hide-on-larger-than-4">
                            {windSpeed}{" "}
                            <span className="secondary-data">
                              {windGust > 0 && <> &nbsp;({windGust})</>}
                            </span>
                            {windDegree && (
                              <img
                                className="wind-degree-icon"
                                width="15px"
                                height="15px"
                                alt={`ikon for vindretning ${windDegree} ${getWindDirectionIcon(
                                  windDegree
                                )}`}
                                src={require(`Assets/img/arrows/${getWindDirectionIcon(
                                  windDegree
                                )}.svg`)}
                              />
                            )}
                          </span>

                          {/* End: Wind on small screens */}
                          {/* Begin: Wind on large screens */}
                          <span className="hide-on-smaller-than-4">
                            {windSpeed !== null && <>{windSpeed} m/s</>}
                            {windDegree && (
                              <img
                                className="wind-degree-icon"
                                width="15px"
                                height="15px"
                                alt={`ikon for vindretning ${windDegree} ${getWindDirectionIcon(
                                  windDegree
                                )}`}
                                src={require(`Assets/img/arrows/${getWindDirectionIcon(
                                  windDegree
                                )}.svg`)}
                              />
                            )}
                            <span className="secondary-data">
                              {windGust > 0 && <>({windGust} m/s)</>}
                            </span>
                          </span>
                          {/* End: Wind on large screens */}
                        </p>
                        {/* End: wind */}
                        <p className="small-column column-base-style hide-on-smaller-than-5">
                          {humidity && <>{humidity}%</>}
                        </p>
                      </AccordionDetails>
                    )
                  )}
                {/* End: Hour details in expanded panel */}
              </Accordion>
            )
          )}
        </div>
      )}
      {/*TODO: make error handling*/}
      {/* {!weatherData.length && (
        <div>
          <h2>Kunne ikke hente data</h2>
        </div>
      )} */}
    </>
  );
};
export { WeatherByHoursAccordion };
